<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { VideoUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  import { isValidUrl } from '@common/utils/validation';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let currentRulenames = [];
  let invalidRulename = false;
  let errorMessage = '';
  let rulename = '';
  let uploadError = false;
  let url = '';
  let validationError = false;

  const onValidation = () => {
    validationError = !isValidUrl(url);

    if (validationError) {
      errorMessage = $format('label.VALIDATION_ERROR_NO_VALID_URL');
    }
  };

  const executeCreateVideo = async () => {
    uploadError = false;

    $appStore.loading = true;

    const data = {
      rulename: rulename,
      url: url
    };

    const response = await AssetsService.addVideo(data);

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: rulename } }), 'error');

      uploadError = true;
      errorMessage = response.message;

      throw new Error($format('label.FAILURE.CREATE_VIDEO'));
    }

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: rulename } }));

    step.data = {
      id: rulename
    };

    return response;
  };

  const rollbackCreateVideo = async () => {
    if (!step.data?.id) {
      return;
    }

    $appStore.loading = true;

    try {
      const videos = await AssetsService.getAllVideos();
      const video = videos.find((vid) => vid.rulename === step.data.id);

      if (video) {
        await AssetsService.deleteVideo(video);
      }
    } catch (error) {
      console.error('Failed to rollback video creation:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  const getCurrentRulenames = async () => {
    const response = await AssetsService.getAllVideos();
    currentRulenames = Array.isArray(response) ? response.map(({ rulename }) => rulename) : [];
  };

  onMount(() => {
    getCurrentRulenames();

    if (onExecute) {
      onExecute(executeCreateVideo);
    }
    if (onRollback) {
      onRollback(rollbackCreateVideo);
    }
  });

  $: invalidRulename = currentRulenames.includes(rulename.toLowerCase());
  $: nextDisabled = !url.length || invalidRulename || validationError;
</script>

<div class="video-create-form">
  <VideoUploadForm
    bind:rulename
    bind:url
    bind:invalidRulename
    bind:uploadError
    bind:validationError
    bind:errorMessage
    bind:currentRulenames
    {onValidation}
  />
</div>

<style lang="scss">
  .video-create-form {
    width: 100%;
  }
</style>
